import { IWasmCustomData, TWasmId } from "~/interfaces";
import Wasm from "../wasm";
import { IWasmStoreMarker, TMarkerVisualType } from "../store/interfaces";
import { getWasmIntercationType } from "../utils";
import { MAIN_TEXTURES, CUSTOM_TEXTURES } from "../const";

type TVisualMapping = {
	[key in TMarkerVisualType]?: TWasmId;
}

interface IOpts {
	customTexture?: "position-share-device" | "position-share-image" | "position-share-image-arrow" | "position-share-image-radius";
}

export default class WasmMapMarker {
	#wasmVisuals: TVisualMapping;

	#wasmHoverVisuals: TVisualMapping;

	// vsechny wasm id
	#wasmIds: Array<TWasmId>;

	#wasm: Wasm;

	#data: IWasmStoreMarker;

	#customData: IWasmCustomData;

	#opts: IOpts;

	// normal, active
	#zIndex: Array<number>;

	#isHover: boolean;

	constructor(wasm: Wasm, data: IWasmStoreMarker, opts?: IOpts) {
		this.#wasmVisuals = {};
		this.#wasmHoverVisuals = {};
		this.#wasmIds = [];
		this.#wasm = wasm;
		this.#data = structuredClone(data);
		this.#customData = {
			url: this.#data.img,
			...data.customData,
		};
		this.#opts = {
			...opts,
		};
		this.#zIndex = [0, 0];
		this.#isHover = false;

		if (this.#data.zIndex) {
			if (Array.isArray(this.#data.zIndex)) {
				this.#zIndex = this.#data.zIndex;
			} else {
				this.#zIndex = [this.#data.zIndex, this.#data.zIndex];
			}
		}

		this.#build();
		this.#updateVisual();

		if (this.#data.angle !== 0) {
			this.setAngle(this.#data.angle);
		}
	}

	#destroy(wasmIds: Array<TWasmId>) {
		for (const wasmId of wasmIds) {
			this.#wasm.mapContent.remove(wasmId);
		}
	}

	destroy() {
		if (this.#data.hover) {
			this.#wasm.removeHoverId(this.#data.id);
		}

		this.#destroy(this.#wasmIds);
		this.#wasm.entityChange();
	}

	setPosition(wgsLon: number, wgsLat: number) {
		const mercPos = this.#wasm.module.GeoMath.wgsToMercator({
			x: wgsLon,
			y: wgsLat,
		});

		this.#data.lon = wgsLon;
		this.#data.lat = wgsLat;

		for (const wasmId of this.#wasmIds) {
			this.#wasm.mapContent.setPointPosMerc(wasmId, mercPos.x, mercPos.y);
		}

		this.#wasm.entityChange();
	}

	setVisual(visual: TMarkerVisualType) {
		this.#data.visual = visual;

		this.#updateVisual();
	}

	get wasmIds() {
		return this.#wasmIds;
	}

	setVisibility(visible: boolean) {
		this.#data.visible = visible;

		this.#updateVisual();
	}

	setAngle(angle: number) {
		this.#data.angle = -angle;

		for (const wasmId of this.#wasmIds) {
			this.#wasm.mapContent.setPointAngle(wasmId, this.#data.angle);
		}

		this.#wasm.entityChange();
	}

	setBringToTheFront(bringToTheFront: boolean) {
		this.#data.bringToTheFront = bringToTheFront;

		const zIndex = this.#getZindex(bringToTheFront);

		this.#wasm.mapContent.setRenderLayer(this.#wasmIds, zIndex);
		this.#wasm.entityChange();
	}

	setHover(state: boolean) {
		if (!this.#data.hover || this.#isHover === state) {
			return 0;
		}

		this.#isHover = state;

		const curId = this.#data.visual === "standard"
			? this.#wasmHoverVisuals.standard
			: this.#wasmHoverVisuals.inactive;

		if (this.#isHover) {
			const allIds = Object.values(this.#wasmVisuals);

			this.#wasm.mapContent.setIsVisible(allIds, false);
		} else {
			// reset
			this.#updateVisual();
		}

		this.#wasm.mapContent.setIsVisible([curId], this.#isHover);

		return curId;
	}

	#build() {
		const icons = this.#wasm.icons;

		this.#wasmIds = [];

		switch (this.#data.type) {
			case "geo-location": {
				const activeTexture = icons.get(CUSTOM_TEXTURES["geo-location"]);

				this.#wasmVisuals.standard = this.#addMarker(activeTexture);
				break;
			}

			case "user-marks": {
				const activeTexture = icons.get(CUSTOM_TEXTURES["user-marks-active"]);
				const inactiveTexture = icons.get(CUSTOM_TEXTURES["user-marks-inactive"]);
				const opacityTexture = icons.get(CUSTOM_TEXTURES["user-marks-opacity-active"]);

				this.#wasmVisuals.standard = this.#addMarker(activeTexture);
				this.#wasmVisuals.inactive = this.#addMarker(inactiveTexture);
				this.#wasmVisuals.opacity = this.#addMarker(opacityTexture);
				break;
			}

			case "measure-start": {
				const activeTexture = icons.get(MAIN_TEXTURES["measure-start-active"]);
				const inactiveTexture = icons.get(MAIN_TEXTURES["measure-start-inactive"]);

				this.#wasmVisuals.standard = this.#addMarker(activeTexture);
				this.#wasmVisuals.inactive = this.#addMarker(inactiveTexture);
				break;
			}

			case "measure-points": {
				const activeTexture = icons.get(MAIN_TEXTURES["measure-points-active"]);
				const inactiveTexture = icons.get(MAIN_TEXTURES["measure-points-inactive"]);

				this.#wasmVisuals.standard = this.#addMarker(activeTexture);
				this.#wasmVisuals.inactive = this.#addMarker(inactiveTexture);
				break;
			}

			case "height-profile": {
				const activeTexture = icons.get(MAIN_TEXTURES["height-profile"]);

				this.#wasmVisuals.standard = this.#addMarker(activeTexture);
				break;
			}

			case "small": {
				const activeTexture = icons.get(CUSTOM_TEXTURES["small-active"]);
				const inactiveTexture = icons.get(CUSTOM_TEXTURES["small-inactive"]);

				this.#wasmVisuals.standard = this.#addMarker(activeTexture);
				this.#wasmVisuals.inactive = this.#addMarker(inactiveTexture);
				break;
			}

			case "small-gray": {
				const activeTexture = icons.get(CUSTOM_TEXTURES["small-gray"]);

				this.#wasmVisuals.standard = this.#addMarker(activeTexture);
				break;
			}

			case "route-start": {
				const activeTexture = icons.get(CUSTOM_TEXTURES["route-start-active"]);
				const inactiveTexture = icons.get(CUSTOM_TEXTURES["route-start-inactive"]);
				const opacityTexture = icons.get(CUSTOM_TEXTURES["route-start-opacity-active"]);

				this.#wasmVisuals.standard = this.#addMarker(activeTexture);
				this.#wasmVisuals.inactive = this.#addMarker(inactiveTexture);
				this.#wasmVisuals.opacity = this.#addMarker(opacityTexture);
				break;
			}

			case "route-end": {
				const activeTexture = icons.get(CUSTOM_TEXTURES["route-end-active"]);
				const inactiveTexture = icons.get(CUSTOM_TEXTURES["route-end-inactive"]);
				const opacityTexture = icons.get(CUSTOM_TEXTURES["route-end-opacity-active"]);

				this.#wasmVisuals.standard = this.#addMarker(activeTexture);
				this.#wasmVisuals.inactive = this.#addMarker(inactiveTexture);
				this.#wasmVisuals.opacity = this.#addMarker(opacityTexture);
				break;
			}

			case "big": {
				const activeTexture = icons.get(MAIN_TEXTURES["big-active"]);
				const inactiveTexture = icons.get(MAIN_TEXTURES["big-inactive"]);

				this.#wasmVisuals.standard = this.#addMarker(activeTexture);
				this.#wasmVisuals.inactive = this.#addMarker(inactiveTexture);
				break;
			}

			case "little": {
				const activeTexture = icons.get(MAIN_TEXTURES["little-active"]);
				const inactiveTexture = icons.get(MAIN_TEXTURES["little-inactive"]);

				this.#wasmVisuals.standard = this.#addMarker(activeTexture);
				this.#wasmVisuals.inactive = this.#addMarker(inactiveTexture);
				break;
			}

			case "always-on-map": {
				const activeTexture = icons.get(MAIN_TEXTURES["always-on-map"]);

				this.#wasmVisuals.standard = this.#addMarker(activeTexture);
				break;
			}

			case "pano": {
				const activeTexture = icons.get(MAIN_TEXTURES.pano);

				this.#wasmVisuals.standard = this.#addMarker(activeTexture);
				break;
			}

			case "search-badge-text": {
				const activeTexture = icons.get(CUSTOM_TEXTURES["search-badge-text-active"]);
				const inactiveTexture = icons.get(CUSTOM_TEXTURES["search-badge-text-inactive"]);

				this.#wasmVisuals.standard = this.#addMarker(activeTexture);
				this.#wasmVisuals.inactive = this.#addMarker(inactiveTexture);

				// nastavime kolizni poradi
				if (this.#customData.order) {
					this.#wasm.mapContent.setCollisionOrder(this.#wasmVisuals.standard, this.#customData.order);
					this.#wasm.mapContent.setCollisionOrder(this.#wasmVisuals.inactive, this.#customData.order);
				}
				break;
			}

			case "small-universal": {
				const activeTexture = icons.get(MAIN_TEXTURES["small-universal-active"]);
				const inactiveTexture = icons.get(MAIN_TEXTURES["small-universal-inactive"]);

				this.#wasmVisuals.standard = this.#addMarker(activeTexture);
				this.#wasmVisuals.inactive = this.#addMarker(inactiveTexture);
				break;
			}

			case "big-universal": {
				const activeTexture = icons.get(MAIN_TEXTURES["big-universal-active"]);
				const inactiveTexture = icons.get(MAIN_TEXTURES["big-universal-inactive"]);

				this.#wasmVisuals.standard = this.#addMarker(activeTexture);
				this.#wasmVisuals.inactive = this.#addMarker(inactiveTexture);
				break;
			}

			case "photo": {
				const activeTexture = icons.get(CUSTOM_TEXTURES["photo-active"]);
				const inactiveTexture = icons.get(CUSTOM_TEXTURES["photo-inactive"]);

				this.#wasmVisuals.standard = this.#addMarker(activeTexture);
				this.#wasmVisuals.inactive = this.#addMarker(inactiveTexture);
				break;
			}

			case "webcam": {
				const activeTexture = icons.get(CUSTOM_TEXTURES["webcam-active"]);
				const inactiveTexture = icons.get(CUSTOM_TEXTURES["webcam-inactive"]);

				this.#wasmVisuals.standard = this.#addMarker(activeTexture);
				this.#wasmVisuals.inactive = this.#addMarker(inactiveTexture);
				break;
			}

			case "path-caption": {
				const activeTexture = icons.get(CUSTOM_TEXTURES["path-caption"]);

				this.#wasmVisuals.standard = this.#addMarker(activeTexture);
				break;
			}

			case "route-point-circle": {
				const activeTexture = icons.get(CUSTOM_TEXTURES["route-point-circle"]);

				this.#wasmVisuals.standard = this.#addMarker(activeTexture);
				break;
			}

			case "route-popup-info": {
				const activeTexture = icons.get(CUSTOM_TEXTURES["route-popup-info-blue"]);
				const inactiveTexture = icons.get(CUSTOM_TEXTURES["route-popup-info-white"]);

				this.#wasmVisuals.standard = this.#addMarker(activeTexture);
				this.#wasmVisuals.inactive = this.#addMarker(inactiveTexture);
				break;
			}

			case "route-popup-weather": {
				const activeTexture = icons.get(CUSTOM_TEXTURES["route-popup-weather-temp"]);
				const weatherPrecpTexture = icons.get(CUSTOM_TEXTURES["route-popup-weather-precp"]);
				const weatherWindTexture = icons.get(CUSTOM_TEXTURES["route-popup-weather-wind"]);

				this.#wasmVisuals.standard = this.#addMarker(activeTexture);
				this.#wasmVisuals["weather-precp"] = this.#addMarker(weatherPrecpTexture);
				this.#wasmVisuals["weather-wind"] = this.#addMarker(weatherWindTexture);
				break;
			}

			case "route-popup-pubt-summary": {
				const activeTexture = icons.get(CUSTOM_TEXTURES["route-popup-pubt-summary"]);

				this.#wasmVisuals.standard = this.#addMarker(activeTexture);
				break;
			}

			case "route-popup-pubt-section": {
				const activeTexture = icons.get(CUSTOM_TEXTURES["route-popup-pubt-section"]);

				this.#wasmVisuals.standard = this.#addMarker(activeTexture);
				break;
			}

			case "route-popup-closure": {
				const activeTexture = icons.get(CUSTOM_TEXTURES["route-popup-closure"]);

				this.#wasmVisuals.standard = this.#addMarker(activeTexture);
				break;
			}

			default: {
				// vlastni textury
				if (this.#opts.customTexture) {
					switch (this.#opts.customTexture) {
						case "position-share-device": {
							const activeTexture = icons.get(CUSTOM_TEXTURES["position-share-device-active"]);
							const inactiveTexture = icons.get(CUSTOM_TEXTURES["position-share-device-inactive"]);

							this.#wasmVisuals.standard = this.#addMarker(activeTexture);
							this.#wasmVisuals.inactive = this.#addMarker(inactiveTexture);
							break;
						}

						case "position-share-image": {
							const activeTexture = icons.get(CUSTOM_TEXTURES["position-share-image-active"]);
							const inactiveTexture = icons.get(CUSTOM_TEXTURES["position-share-image-inactive"]);
							const grayActiveTexture = icons.get(CUSTOM_TEXTURES["position-share-image-gray-active"]);
							const grayinactiveTexture = icons.get(CUSTOM_TEXTURES["position-share-image-gray-inactive"]);

							this.#wasmVisuals.standard = this.#addMarker(activeTexture);
							this.#wasmVisuals.inactive = this.#addMarker(inactiveTexture);
							this.#wasmVisuals["position-gray-active"] = this.#addMarker(grayActiveTexture);
							this.#wasmVisuals["position-gray-inactive"] = this.#addMarker(grayinactiveTexture);
							break;
						}

						case "position-share-image-arrow": {
							const activeTexture = icons.get(MAIN_TEXTURES["position-share-image-arrow"]);

							this.#wasmVisuals.standard = this.#addMarker(activeTexture);
							break;
						}

						case "position-share-image-radius": {
							const activeTexture = icons.get(CUSTOM_TEXTURES["position-share-image-radius"]);

							this.#wasmVisuals.standard = this.#addMarker(activeTexture);
							break;
						}

						default:
					}
				}

				// nebylo nastaveno
				if (!this.#wasmVisuals.standard) {
					const activeTexture = icons.get(MAIN_TEXTURES["big-active"]);
					const inactiveTexture = icons.get(MAIN_TEXTURES["big-inactive"]);

					this.#wasmVisuals.standard = this.#addMarker(activeTexture);
					this.#wasmVisuals.inactive = this.#addMarker(inactiveTexture);
				}
			}
		}

		const allVisuals = Object.keys(this.#wasmVisuals);

		for (const visual of allVisuals) {
			this.#wasmIds.push(this.#wasmVisuals[visual]);
		}

		// speciality pro hover; hnedka skryjeme
		if (this.#data.hover) {
			switch (this.#data.hover.type) {
				case "small": {
					const activeTexture = icons.get(CUSTOM_TEXTURES["small-active"]);
					const inactiveTexture = icons.get(CUSTOM_TEXTURES["small-inactive"]);

					this.#wasmHoverVisuals.standard = this.#addMarker(activeTexture);
					this.#wasmHoverVisuals.inactive = this.#addMarker(inactiveTexture);
					break;
				}

				case "big": {
					const activeTexture = icons.get(MAIN_TEXTURES["big-active"]);
					const inactiveTexture = icons.get(MAIN_TEXTURES["big-inactive"]);

					this.#wasmHoverVisuals.standard = this.#addMarker(activeTexture);
					this.#wasmHoverVisuals.inactive = this.#addMarker(inactiveTexture);
					break;
				}

				default:
			}

			const hoverIds = [this.#wasmHoverVisuals.standard, this.#wasmHoverVisuals.inactive];

			// skryjeme
			this.#wasm.mapContent.setIsVisible(hoverIds, false);
			//  zindexy
			this.#wasm.mapContent.setRenderLayer(hoverIds, this.#data.hover.zIndex);
			// pridame do pole
			this.#wasmIds.push(...hoverIds);
		}

		this.#wasm.entityChange();
	}

	#addMarker(texture: number) {
		if (!texture) {
			/* eslint-disable-next-line */
			console.error(`Missing texture for type ${this.#data.type}!`);

			return -1;
		}

		const interactionType = getWasmIntercationType(this.#wasm.module, this.#data.interactionType);

		return this.#wasm.mapContent.addPoint(this.#data.lon, this.#data.lat, texture, this.#getZindex(this.#data.bringToTheFront), interactionType, this.#customData);
	}

	#updateVisual() {
		const allVisuals = Object.keys(this.#wasmVisuals);
		const visibleIds: Array<TWasmId> = [];
		const hiddenIds: Array<TWasmId> = [];
		let curVisual = this.#data.visual;

		if (!(curVisual in this.#wasmVisuals)) {
			curVisual = "standard";
		}

		for (const visual of allVisuals) {
			const wasmId = this.#wasmVisuals[visual];

			if (this.#data.visible) {
				if (curVisual === visual) {
					visibleIds.push(wasmId);
				} else {
					hiddenIds.push(wasmId);
				}
			} else {
				hiddenIds.push(wasmId);
			}
		}

		visibleIds.length > 0 && this.#wasm.mapContent.setIsVisible(visibleIds, true);
		hiddenIds.length > 0 && this.#wasm.mapContent.setIsVisible(hiddenIds, false);

		this.#wasm.entityChange();
	}

	#getZindex(isHover?: boolean) {
		return this.#zIndex[isHover ? 1 : 0];
	}
}
